import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Clipboard,
  Eye,
  KeyRound,
  LogOut,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserCheck,
  UsersRound,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import ProfileDrawer from '../../../components/reflect/staff/ProfileDrawer';
import ReflectStaffShell, {
  StaffError,
  StaffPageLoading,
  staffRequest,
} from '../../../components/reflect/staff/ReflectStaffShell';
import {
  StaffPanel,
  StaffSwitch,
  StatusChip,
  TypedConfirmDialog,
} from '../../../components/reflect/staff/StaffControls';
import { parseRosterPaste, sortRosterByStatus } from '../../../utils/reflectStaff';

const inputClasses = [
  'min-h-12 w-full rounded-xl border border-white/15 bg-black/30 px-4 text-white outline-none',
  'placeholder:text-white/30 focus:border-cyan focus:ring-2 focus:ring-cyan/25',
].join(' ');

const RHYME_MODES = [
  ['clean', 'Clean', 'No cuss words. Middle school default.'],
  ['real', 'Real', 'Cussing shows, no violence or sexual words. High school default.'],
  ['full', 'Full', 'Everything except slurs. Adult classes.'],
];

const ReflectClassroom = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classroom, setClassroom] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [singleName, setSingleName] = useState('');
  const [bulkNames, setBulkNames] = useState('');
  const [rosterErrors, setRosterErrors] = useState({});
  const [copied, setCopied] = useState(false);
  const [profileStudent, setProfileStudent] = useState(null);
  const [deleteStudent, setDeleteStudent] = useState(null);
  const [deleteClassOpen, setDeleteClassOpen] = useState(false);
  const [confirmValue, setConfirmValue] = useState('');

  const loadClass = useCallback(async (silent = false) => {
    if (!silent) setError('');
    try {
      const data = await staffRequest(`/api/reflect/classes/${classId}`);
      setClassroom(data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [classId]);

  const loadAlerts = useCallback(async () => {
    try {
      setAlerts(await staffRequest('/api/reflect/alerts'));
    } catch (_error) {
      // The class remains usable if the header badge cannot refresh.
    }
  }, []);

  useEffect(() => {
    loadClass();
    loadAlerts();

    const classInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadClass(true);
    }, 5000);
    const alertInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadAlerts();
    }, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadClass(true);
        loadAlerts();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(classInterval);
      window.clearInterval(alertInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadAlerts, loadClass]);

  const roster = useMemo(() => sortRosterByStatus(classroom?.roster || []), [classroom]);

  const updateJoining = async (joiningOpen) => {
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}/joining`, {
        method: 'PUT',
        body: JSON.stringify({ joining_open: joiningOpen }),
      });
      setClassroom((current) => ({ ...current, joining_open: joiningOpen }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const updateRhymeMode = async (rhymeMode) => {
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}/rhyme-mode`, {
        method: 'PUT',
        body: JSON.stringify({ rhyme_mode: rhymeMode }),
      });
      setClassroom((current) => ({
        ...current,
        rhyme_mode: rhymeMode,
        clean_rhymes: rhymeMode === 'clean',
      }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const rhymeMode = classroom?.rhyme_mode ?? (classroom?.clean_rhymes ? 'clean' : 'real');

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(classroom.join_code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_error) {
      setError('Copy did not work. Select the code and copy it manually.');
    }
  };

  const rotateCode = async () => {
    if (!window.confirm('Create a new code? The old code stops working.')) return;
    setBusy(true);
    setError('');
    try {
      const data = await staffRequest(`/api/reflect/classes/${classId}/join-code/rotate`, {
        method: 'POST',
      });
      setClassroom((current) => ({ ...current, join_code: data.join_code }));
      setNotice('New join code ready.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const addNames = async (names) => {
    if (!names.length) return;
    setBusy(true);
    setError('');
    setNotice('');
    const nextErrors = {};
    let added = 0;
    for (const name of names) {
      try {
        await staffRequest(`/api/reflect/classes/${classId}/roster`, {
          method: 'POST',
          body: JSON.stringify({ display_name: name }),
        });
        added += 1;
      } catch (requestError) {
        nextErrors[name] = requestError.message;
      }
    }
    setRosterErrors(nextErrors);
    if (added) {
      setNotice(`${added} ${added === 1 ? 'student' : 'students'} added.`);
      await loadClass(true);
    }
    setBusy(false);
  };

  const addOne = async (event) => {
    event.preventDefault();
    const name = singleName.trim();
    if (!name) return;
    await addNames([name]);
    setSingleName('');
  };

  const addMany = async (event) => {
    event.preventDefault();
    const names = parseRosterPaste(bulkNames);
    await addNames(names);
    if (names.length) setBulkNames('');
  };

  const approve = async (student) => {
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}/roster/${student.id}/approve`, {
        method: 'POST',
      });
      setNotice(`${student.display_name} is approved.`);
      await loadClass(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async (student) => {
    const confirmed = window.confirm(
      `Reset ${student.display_name}'s PIN? They pick a new PIN; their saved answers are deleted.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}/roster/${student.id}/reset`, {
        method: 'POST',
      });
      setNotice(`${student.display_name} can pick a new PIN.`);
      await loadClass(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const signEveryoneOut = async () => {
    if (!window.confirm('Sign everyone out of this class? They will need their PIN to return.')) return;
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}/tokens/revoke`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setNotice('Everyone has been signed out.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteStudent = async () => {
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}/roster/${deleteStudent.id}`, {
        method: 'DELETE',
      });
      setNotice(`${deleteStudent.display_name} was deleted.`);
      setDeleteStudent(null);
      setConfirmValue('');
      await loadClass(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteClass = async () => {
    setBusy(true);
    setError('');
    try {
      await staffRequest(`/api/reflect/classes/${classId}`, { method: 'DELETE' });
      navigate('/admin/reflect', { replace: true });
    } catch (requestError) {
      setError(requestError.message);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <ReflectStaffShell title="Class studio" alerts={alerts} backTo="/admin/reflect" backLabel="All classes">
        <StaffPageLoading line="Opening the class..." />
      </ReflectStaffShell>
    );
  }

  return (
    <ReflectStaffShell
      title={classroom?.name || 'Class not available'}
      subtitle={classroom ? 'Keep this screen handy while students join and work.' : ''}
      alerts={alerts}
      backTo="/admin/reflect"
      backLabel="All classes"
    >
      <div className="space-y-4">
        <StaffError>{error}</StaffError>
        {notice && (
          <div role="status" className="rounded-2xl border border-cyan/30 bg-cyan/10 px-4 py-3 font-bold text-cyan">
            {notice}
          </div>
        )}
      </div>

      {classroom && (
        <div className="mt-6 space-y-6">
          <StaffPanel className="overflow-hidden border-cyan/25 bg-cyan/[0.035]">
            <div className="grid items-center gap-7 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="text-center lg:text-left">
                <p className="text-sm font-black uppercase tracking-[0.2em] text-white/45">Join code</p>
                <button
                  type="button"
                  onClick={copyCode}
                  className="mt-2 max-w-full rounded-2xl px-3 font-heading text-5xl font-black
                    tracking-[0.22em] text-cyan hover:bg-white/[0.04] focus:outline-none
                    focus-visible:ring-2 focus-visible:ring-cyan sm:text-7xl"
                  aria-label={`Copy join code ${classroom.join_code}`}
                >
                  {classroom.join_code}
                </button>
                <p className="mt-3 text-lg font-bold text-white/70">
                  Open studio.letthebeatbuild.me/reflect
                </p>
              </div>
              <div className="space-y-4 rounded-2xl border border-white/10 bg-black/25 p-4">
                <StaffSwitch
                  checked={Boolean(classroom.joining_open)}
                  disabled={busy}
                  onChange={updateJoining}
                  label="Joining open"
                  description="Students can use the code while this is on."
                />
                <div className="border-t border-white/10 pt-4">
                  <p className="font-heading text-lg font-black">Rhyme Brain</p>
                  <div
                    className="mt-3 grid grid-cols-3 rounded-xl border border-white/15 p-1"
                    role="group"
                    aria-label="Rhyme Brain mode"
                  >
                    {RHYME_MODES.map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        disabled={busy}
                        aria-pressed={rhymeMode === value}
                        onClick={() => updateRhymeMode(value)}
                        className={`min-h-11 rounded-lg px-2 text-sm font-black
                          disabled:opacity-40 ${rhymeMode === value
                            ? 'bg-cyan text-black' : 'text-white/60 hover:bg-white/[0.06]'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">
                    {RHYME_MODES.find(([value]) => value === rhymeMode)?.[2]}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={copyCode}
                    className="min-h-11 rounded-full border-white/15 bg-transparent hover:bg-white/10"
                  >
                    {copied ? <Check className="mr-2 h-4 w-4" /> : <Clipboard className="mr-2 h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={rotateCode}
                    disabled={busy}
                    className="min-h-11 rounded-full border-white/15 bg-transparent hover:bg-white/10"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    New code
                  </Button>
                </div>
              </div>
            </div>
          </StaffPanel>

          <div className="grid items-start gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]">
            <div className="space-y-6 xl:sticky xl:top-6">
              <StaffPanel>
                <div className="flex items-center gap-3">
                  <Plus className="h-5 w-5 text-cyan" />
                  <h2 className="font-heading text-xl font-black">Add students</h2>
                </div>
                <form onSubmit={addOne} className="mt-5">
                  <label htmlFor="single-student" className="font-bold">One at a time</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      id="single-student"
                      value={singleName}
                      onChange={(event) => setSingleName(event.target.value)}
                      maxLength={40}
                      placeholder="First name + last initial"
                      className={inputClasses}
                    />
                    <Button
                      type="submit"
                      disabled={busy || !singleName.trim()}
                      className="min-h-12 rounded-xl bg-cyan px-4 font-black text-black hover:bg-cyan/90"
                    >
                      Add
                    </Button>
                  </div>
                </form>
                <form onSubmit={addMany} className="mt-6 border-t border-white/10 pt-5">
                  <label htmlFor="bulk-students" className="font-bold">Paste many</label>
                  <p className="mt-1 text-sm text-white/45">One per line, first name + last initial.</p>
                  <textarea
                    id="bulk-students"
                    value={bulkNames}
                    onChange={(event) => setBulkNames(event.target.value)}
                    rows={6}
                    placeholder={'Alex R.\nJordan T.\nSam K.'}
                    className={`${inputClasses} mt-2 py-3`}
                  />
                  <Button
                    type="submit"
                    disabled={busy || !bulkNames.trim()}
                    className="mt-3 min-h-11 w-full rounded-full bg-white font-black text-black hover:bg-white/85"
                  >
                    <UsersRound className="mr-2 h-4 w-4" />
                    Add list
                  </Button>
                </form>
                {Object.keys(rosterErrors).length > 0 && (
                  <div className="mt-4 space-y-2" role="alert">
                    {Object.entries(rosterErrors).map(([name, message]) => (
                      <p
                        key={name}
                        className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-100"
                      >
                        <strong>{name}:</strong> {message}
                      </p>
                    ))}
                  </div>
                )}
              </StaffPanel>

              <StaffPanel>
                <h2 className="font-heading text-lg font-black">Room controls</h2>
                <Button
                  type="button"
                  variant="outline"
                  onClick={signEveryoneOut}
                  disabled={busy}
                  className="mt-4 min-h-11 w-full rounded-full border-white/15 bg-transparent hover:bg-white/10"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign everyone out
                </Button>
              </StaffPanel>
            </div>

            <section aria-labelledby="roster-title">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 id="roster-title" className="font-heading text-2xl font-black">Roster</h2>
                <span className="text-sm font-bold text-white/45">
                  {roster.length} {roster.length === 1 ? 'student' : 'students'}
                </span>
              </div>
              {roster.length ? (
                <div className="space-y-3">
                  {roster.map((student) => (
                    <article
                      key={student.id}
                      className={`rounded-2xl border bg-[#0d1011]/95 p-4
                        ${student.status === 'pending'
                          ? 'animate-pulse border-cyan/45 shadow-[0_0_28px_-16px_rgba(0,255,221,0.8)]'
                          : 'border-white/10'}`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                              bg-white/[0.06]"
                          >
                            {student.status === 'pending'
                              ? <ShieldCheck className="h-5 w-5 text-cyan" />
                              : <UsersRound className="h-5 w-5 text-white/45" />}
                          </span>
                          <div className="min-w-0">
                            <h3 className="truncate font-heading text-lg font-black">{student.display_name}</h3>
                            <div className="mt-1"><StatusChip status={student.status} /></div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 lg:justify-end">
                          {student.status === 'pending' && (
                            <Button
                              type="button"
                              onClick={() => approve(student)}
                              disabled={busy}
                              className="min-h-11 rounded-full bg-cyan px-4 font-black text-black hover:bg-cyan/90"
                            >
                              <UserCheck className="mr-2 h-4 w-4" />
                              Approve
                            </Button>
                          )}
                          {student.status !== 'unclaimed' && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => resetPin(student)}
                              disabled={busy}
                              className="min-h-11 rounded-full border-white/15 bg-transparent hover:bg-white/10"
                            >
                              <KeyRound className="mr-2 h-4 w-4" />
                              Reset PIN
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setProfileStudent(student)}
                            className="min-h-11 rounded-full border-white/15 bg-transparent hover:bg-white/10"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View profile
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              setConfirmValue('');
                              setDeleteStudent(student);
                            }}
                            className="min-h-11 rounded-full text-red-200 hover:bg-red-500/10 hover:text-red-100"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete student
                          </Button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <StaffPanel className="min-h-52 text-center">
                  <img src="/pizzul/happy.png" alt="Pizzul" className="mx-auto h-24 w-24 object-contain" />
                  <h3 className="mt-2 font-heading text-xl font-black">The roster is ready for names</h3>
                  <p className="mt-2 text-white/50">Add one student or paste the whole class.</p>
                </StaffPanel>
              )}
            </section>
          </div>

          <StaffPanel className="border-red-400/20">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-heading text-lg font-black">Delete class</h2>
                <p className="mt-1 text-sm text-white/50">Deletes every student and everything they wrote.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConfirmValue('');
                  setDeleteClassOpen(true);
                }}
                className="min-h-11 rounded-full border-red-400/35 bg-transparent text-red-200 hover:bg-red-500/10"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete class
              </Button>
            </div>
          </StaffPanel>
        </div>
      )}

      <ProfileDrawer classId={classId} student={profileStudent} onClose={() => setProfileStudent(null)} />

      <TypedConfirmDialog
        open={Boolean(deleteStudent)}
        title={`Delete ${deleteStudent?.display_name || 'student'}?`}
        description="Deletes everything they wrote. This can't be undone."
        confirmText={deleteStudent?.display_name || ''}
        confirmLabel="Delete student"
        value={confirmValue}
        onValueChange={setConfirmValue}
        onCancel={() => {
          setDeleteStudent(null);
          setConfirmValue('');
        }}
        onConfirm={confirmDeleteStudent}
        busy={busy}
      />

      <TypedConfirmDialog
        open={deleteClassOpen}
        title={`Delete ${classroom?.name || 'class'}?`}
        description="Deletes the class, every student, and everything they wrote. This can't be undone."
        confirmText={classroom?.name || ''}
        confirmLabel="Delete class"
        value={confirmValue}
        onValueChange={setConfirmValue}
        onCancel={() => {
          setDeleteClassOpen(false);
          setConfirmValue('');
        }}
        onConfirm={confirmDeleteClass}
        busy={busy}
      />
    </ReflectStaffShell>
  );
};

export default ReflectClassroom;
